/**
 * Modèles d'export : plusieurs modèles nommés peuvent partager un même livrable
 * (`docs/DECISIONS.md` D81), chacun avec son propre jeu de colonnes.
 *
 * « Livrable » (la source de données, ex. « Export opérationnel ») est distinct de « Modèle » (le
 * libellé propre à ce modèle nommé) — pour les six modèles par défaut, les deux affichent le même
 * texte, tant que personne n'en a créé un second sur le même livrable.
 *
 * L'édition en ligne reprend le patron de `EcranReferentiels.tsx` (D72) : la ligne bascule en
 * formulaire, boutons Enregistrer/Annuler. « Supprimer » désactive sans jamais retirer la ligne
 * (aucun rôle IAM de ce projet n'a `DeleteItem`, D15) — la réenregistrer via « Modifier » la
 * réactive, comme pour un référentiel désactivé.
 */
import { useState } from "react";

import { ErreurApi } from "../../shared/api/erreurs";
import { Chargement, Erreur, Vide } from "../../shared/composants/Etats";
import type { ModeleExportPublie } from "../../shared/api/types";
import {
  useCreerModeleExport,
  useDesactiverModeleExport,
  useModelesExport,
  useModifierModeleExport,
  useSourcesExport,
} from "./requetes";

type Brouillon = { libelle: string; colonnes: string[]; destinataires: string };

function brouillonVide(): Brouillon {
  return { libelle: "", colonnes: [], destinataires: "" };
}

export function EcranModelesExport() {
  const requete = useModelesExport();
  const sources = useSourcesExport();
  const creer = useCreerModeleExport();
  const modifier = useModifierModeleExport();
  const desactiver = useDesactiverModeleExport();

  const [enCreation, definirEnCreation] = useState(false);
  const [typeRapportChoisi, definirTypeRapportChoisi] = useState("");
  const [ligneEnEdition, definirLigneEnEdition] = useState<string | null>(null);
  const [brouillon, definirBrouillon] = useState<Brouillon>(brouillonVide());
  const [aSupprimer, definirASupprimer] = useState<string | null>(null);

  const modeles = requete.data?.data ?? {};
  const modeleIds = Object.keys(modeles).sort(
    (a, b) => modeles[a]!.type_rapport.localeCompare(modeles[b]!.type_rapport) || a.localeCompare(b),
  );
  const listeSources = sources.data?.data ?? [];
  const colonnesDe = (typeRapport: string) =>
    listeSources.find((s) => s.type_rapport === typeRapport)?.colonnes ?? [];
  const libelleLivrable = (typeRapport: string) =>
    listeSources.find((s) => s.type_rapport === typeRapport)?.libelle ?? typeRapport;

  function basculerColonne(code: string) {
    definirBrouillon((b) => ({
      ...b,
      colonnes: b.colonnes.includes(code) ? b.colonnes.filter((c) => c !== code) : [...b.colonnes, code],
    }));
  }

  function ouvrirCreation() {
    definirEnCreation(true);
    definirLigneEnEdition(null);
    definirTypeRapportChoisi(listeSources[0]?.type_rapport ?? "");
    definirBrouillon({
      ...brouillonVide(),
      colonnes: (listeSources[0]?.colonnes ?? []).map((c) => c.code),
    });
  }

  function changerSourceDeCreation(typeRapport: string) {
    definirTypeRapportChoisi(typeRapport);
    definirBrouillon((b) => ({ ...b, colonnes: colonnesDe(typeRapport).map((c) => c.code) }));
  }

  async function soumettreCreation(evenement: React.FormEvent) {
    evenement.preventDefault();
    await creer.mutateAsync({
      type_rapport: typeRapportChoisi,
      libelle: brouillon.libelle,
      colonnes: brouillon.colonnes,
      destinataires: brouillon.destinataires.split(",").map((d) => d.trim()).filter(Boolean),
    });
    definirEnCreation(false);
    definirBrouillon(brouillonVide());
  }

  function ouvrirEdition(modeleId: string, modele: ModeleExportPublie) {
    definirEnCreation(false);
    definirLigneEnEdition(modeleId);
    const disponibles = colonnesDe(modele.type_rapport).map((c) => c.code);
    definirBrouillon({
      libelle: modele.libelle,
      // Un modèle jamais personnalisé (`colonnes` vide) équivaut à « toutes » : les précocher
      // reflète fidèlement ce que ce rapport produit déjà aujourd'hui.
      colonnes: modele.colonnes.length > 0 ? modele.colonnes : disponibles,
      destinataires: modele.destinataires.join(", "),
    });
  }

  async function soumettreEdition(modeleId: string, evenement: React.FormEvent) {
    evenement.preventDefault();
    await modifier.mutateAsync({
      modeleId,
      corps: {
        libelle: brouillon.libelle,
        colonnes: brouillon.colonnes,
        destinataires: brouillon.destinataires.split(",").map((d) => d.trim()).filter(Boolean),
        // Réenregistrer une ligne la réactive si elle était désactivée : c'est la seule façon de
        // revenir en arrière après « Supprimer », pas de bouton « Réactiver » séparé (même
        // convention que `EcranReferentiels.tsx`, D72).
        actif: true,
      },
    });
    definirLigneEnEdition(null);
  }

  async function confirmerSuppression(modeleId: string) {
    await desactiver.mutateAsync(modeleId);
    definirASupprimer(null);
  }

  if (requete.isPending || sources.isPending) {
    return <Chargement libelle="Chargement des modèles d'export…" />;
  }
  if (requete.isError) {
    return <Erreur erreur={requete.error} reessayer={() => void requete.refetch()} />;
  }
  if (sources.isError) {
    return <Erreur erreur={sources.error} reessayer={() => void sources.refetch()} />;
  }

  const erreur = creer.error ?? modifier.error ?? desactiver.error;

  return (
    <section>
      <h1>Modèles d'export</h1>

      {!enCreation && (
        <button type="button" className="bouton-principal" onClick={ouvrirCreation}>
          + Nouveau modèle
        </button>
      )}

      {enCreation && (
        <form className="formulaire-ligne" onSubmit={(e) => void soumettreCreation(e)}>
          <label htmlFor="nouveau-modele-livrable">Livrable source</label>
          <select
            id="nouveau-modele-livrable"
            value={typeRapportChoisi}
            onChange={(e) => changerSourceDeCreation(e.target.value)}
          >
            {listeSources.map((source) => (
              <option key={source.type_rapport} value={source.type_rapport}>
                {source.libelle}
              </option>
            ))}
          </select>
          <label htmlFor="nouveau-modele-libelle">Libellé</label>
          <input
            id="nouveau-modele-libelle"
            required
            minLength={1}
            value={brouillon.libelle}
            onChange={(e) => definirBrouillon((b) => ({ ...b, libelle: e.target.value }))}
          />
          <label htmlFor="nouveau-modele-destinataires">Destinataires</label>
          <input
            id="nouveau-modele-destinataires"
            placeholder="adresses séparées par des virgules"
            value={brouillon.destinataires}
            onChange={(e) => definirBrouillon((b) => ({ ...b, destinataires: e.target.value }))}
          />

          <fieldset className="colonnes-a-cocher">
            <legend>Colonnes</legend>
            {colonnesDe(typeRapportChoisi).map((colonne) => (
              <label key={colonne.code} className="case-a-cocher">
                <input
                  type="checkbox"
                  checked={brouillon.colonnes.includes(colonne.code)}
                  onChange={() => basculerColonne(colonne.code)}
                />
                {colonne.libelle}
              </label>
            ))}
          </fieldset>

          <div className="formulaire-actions">
            <button type="submit" disabled={creer.isPending || brouillon.colonnes.length === 0}>
              Créer
            </button>
            <button type="button" onClick={() => definirEnCreation(false)}>
              Annuler
            </button>
          </div>
        </form>
      )}

      {modeleIds.length === 0 && <Vide libelle="Aucun modèle d'export." />}
      {modeleIds.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              <th scope="col">Livrable</th>
              <th scope="col">Modèle</th>
              <th scope="col">Colonnes</th>
              <th scope="col">Destinataires</th>
              <th scope="col">Actif</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {modeleIds.map((modeleId) => {
              const modele = modeles[modeleId]!;
              const enEdition = ligneEnEdition === modeleId;

              if (enEdition) {
                return (
                  <tr key={modeleId}>
                    <td>{libelleLivrable(modele.type_rapport)}</td>
                    <td>
                      <input
                        aria-label="Libellé"
                        required
                        minLength={1}
                        value={brouillon.libelle}
                        onChange={(e) =>
                          definirBrouillon((b) => ({ ...b, libelle: e.target.value }))
                        }
                      />
                    </td>
                    <td>
                      <fieldset className="colonnes-a-cocher">
                        <legend>Colonnes</legend>
                        {colonnesDe(modele.type_rapport).map((colonne) => (
                          <label key={colonne.code} className="case-a-cocher">
                            <input
                              type="checkbox"
                              checked={brouillon.colonnes.includes(colonne.code)}
                              onChange={() => basculerColonne(colonne.code)}
                            />
                            {colonne.libelle}
                          </label>
                        ))}
                      </fieldset>
                    </td>
                    <td>
                      <input
                        aria-label="Destinataires"
                        placeholder="adresses séparées par des virgules"
                        value={brouillon.destinataires}
                        onChange={(e) =>
                          definirBrouillon((b) => ({ ...b, destinataires: e.target.value }))
                        }
                      />
                    </td>
                    <td>{modele.actif ? "oui" : "non"}</td>
                    <td>
                      <form
                        className="formulaire-actions"
                        onSubmit={(e) => void soumettreEdition(modeleId, e)}
                      >
                        <button
                          type="submit"
                          disabled={modifier.isPending || brouillon.colonnes.length === 0}
                        >
                          Enregistrer
                        </button>
                        <button type="button" onClick={() => definirLigneEnEdition(null)}>
                          Annuler
                        </button>
                      </form>
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={modeleId}>
                  <td>{libelleLivrable(modele.type_rapport)}</td>
                  <td>{modele.libelle}</td>
                  <td>{modele.colonnes.length > 0 ? modele.colonnes.join(", ") : "toutes"}</td>
                  <td>{modele.destinataires.length > 0 ? modele.destinataires.join(", ") : "—"}</td>
                  <td>{modele.actif ? "oui" : "non"}</td>
                  <td className="formulaire-actions">
                    <button type="button" onClick={() => ouvrirEdition(modeleId, modele)}>
                      Modifier
                    </button>
                    {modele.actif &&
                      (aSupprimer === modeleId ? (
                        <>
                          <span>Supprimer ce modèle ?</span>
                          <button
                            type="button"
                            onClick={() => void confirmerSuppression(modeleId)}
                            disabled={desactiver.isPending}
                          >
                            Confirmer
                          </button>
                          <button type="button" onClick={() => definirASupprimer(null)}>
                            Annuler
                          </button>
                        </>
                      ) : (
                        <button type="button" onClick={() => definirASupprimer(modeleId)}>
                          Supprimer
                        </button>
                      ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {erreur && (
        <p role="alert" className="formulaire-ligne__erreur">
          {erreur instanceof ErreurApi ? erreur.message : "L'opération a échoué."}
        </p>
      )}
    </section>
  );
}
